/**
 * Measure Core Web Vitals (CLS, LCP, INP)
 * Reports to console and returns value for external reporting
 */

export interface PerformanceMetric {
  name: string;
  value: number;
  rating: "good" | "needs-improvement" | "poor";
}

const THRESHOLDS = {
  // Core Web Vitals thresholds
  LCP: { good: 2500, poor: 4000 },
  FID: { good: 100, poor: 300 },
  CLS: { good: 0.1, poor: 0.25 },
  INP: { good: 200, poor: 500 },
  TTFB: { good: 800, poor: 1800 },
};

function getRating(value: number, metric: keyof typeof THRESHOLDS): PerformanceMetric["rating"] {
  const threshold = THRESHOLDS[metric];
  if (value <= threshold.good) return "good";
  if (value <= threshold.poor) return "needs-improvement";
  return "poor";
}

/**
 * Measure Largest Contentful Paint (LCP)
 */
export function observeLCP(callback: (metric: PerformanceMetric) => void) {
  if (!("PerformanceObserver" in window)) return;

  try {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1] as PerformanceEntry & {
        renderTime: number;
        loadTime: number;
      };

      const value = lastEntry.renderTime || lastEntry.loadTime;
      callback({
        name: "LCP",
        value,
        rating: getRating(value, "LCP"),
      });
    });

    observer.observe({ type: "largest-contentful-paint", buffered: true });
  } catch (error) {
    console.warn("LCP observation failed:", error);
  }
}

/**
 * Measure Cumulative Layout Shift (CLS)
 */
export function observeCLS(callback: (metric: PerformanceMetric) => void) {
  if (!("PerformanceObserver" in window)) return;

  let clsValue = 0;

  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if ((entry as any).hadRecentInput) continue;
        clsValue += (entry as any).value;
      }

      callback({
        name: "CLS",
        value: clsValue,
        rating: getRating(clsValue, "CLS"),
      });
    });

    observer.observe({ type: "layout-shift", buffered: true });
  } catch (error) {
    console.warn("CLS observation failed:", error);
  }
}

/**
 * Measure Interaction to Next Paint (INP)
 */
export function observeINP(callback: (metric: PerformanceMetric) => void) {
  if (!("PerformanceObserver" in window)) return;

  let maxDuration = 0;

  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const duration = (entry as any).processingEnd - (entry as any).processingStart;
        if (duration > maxDuration) {
          maxDuration = duration;
          callback({
            name: "INP",
            value: duration,
            rating: getRating(duration, "INP"),
          });
        }
      }
    });

    observer.observe({ type: "event", buffered: true });
  } catch (error) {
    console.warn("INP observation failed:", error);
  }
}

/**
 * Log performance metrics to console (dev only)
 */
export function enablePerformanceMonitoring() {
  if (process.env.NODE_ENV === "production") return;

  observeLCP((metric) => {
    console.log(`[Performance] ${metric.name}: ${metric.value.toFixed(2)}ms (${metric.rating})`);
  });

  observeCLS((metric) => {
    console.log(`[Performance] ${metric.name}: ${metric.value.toFixed(3)} (${metric.rating})`);
  });

  observeINP((metric) => {
    console.log(`[Performance] ${metric.name}: ${metric.value.toFixed(2)}ms (${metric.rating})`);
  });
}

/**
 * Detect layout thrash (expensive reads after writes)
 */
export function detectLayoutThrash() {
  if (process.env.NODE_ENV === "production") return;

  let readAfterWrite = 0;
  const layoutProperties = [
    "offsetTop",
    "offsetLeft",
    "offsetWidth",
    "offsetHeight",
    "scrollTop",
    "scrollLeft",
    "scrollWidth",
    "scrollHeight",
    "clientTop",
    "clientLeft",
    "clientWidth",
    "clientHeight",
  ];

  // Monkey-patch to detect reads
  const originalGetBoundingClientRect = Element.prototype.getBoundingClientRect;
  Element.prototype.getBoundingClientRect = function () {
    readAfterWrite++;
    return originalGetBoundingClientRect.call(this);
  };

  // Warn if excessive layout thrash detected
  setInterval(() => {
    if (readAfterWrite > 100) {
      console.warn(`[Performance] Potential layout thrash detected: ${readAfterWrite} reads in last second`);
    }
    readAfterWrite = 0;
  }, 1000);
}
